import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { UsersHttpRepository } from "../repository";
import { useUsersFilterStore } from "../stores";
import { useDebounce } from "@/hooks/useDebounce";
import useAuth from "@/features/auth/hooks/useAuth";

const PAGE_SIZE = 10;
const usersRepository = new UsersHttpRepository();

export function useUsersList() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const searchValue = useUsersFilterStore((s) => s.searchValue);
  const statusFilter = useUsersFilterStore((s) => s.statusFilter);
  const ordering = useUsersFilterStore((s) => s.ordering);
  const debouncedSearch = useDebounce(searchValue, 500);

  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (debouncedSearch !== prevSearch) {
    setPrevSearch(debouncedSearch);
    setPage(1);
  }

  const [prevStatusFilter, setPrevStatusFilter] = useState(statusFilter);
  if (statusFilter !== prevStatusFilter) {
    setPrevStatusFilter(statusFilter);
    setPage(1);
  }

  const [prevOrdering, setPrevOrdering] = useState(ordering);
  if (ordering !== prevOrdering) {
    setPrevOrdering(ordering);
    setPage(1);
  }

  const query = useQuery({
    queryKey: [
      "users",
      { page, search: debouncedSearch, status: statusFilter, ordering },
    ],
    queryFn: () =>
      usersRepository.fetchList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: (statusFilter as "active" | "inactive") ?? undefined,
        ordering: ordering ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const users = useMemo(() => {
    const result = query.data?.results?.filter((q) => q.id !== user?.id) ?? [];
    return result;
  }, [query.data?.results, user?.id]);

  const pageCount = query.data ? Math.ceil(query.data.count / PAGE_SIZE) : 0;

  return {
    users,
    pageCount,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
  };
}
